import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Button, Card, Center, Group, Loader, Menu, Stack, Text } from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconDownload,
  IconEye,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconRocket,
  IconSettings,
  IconSparkles,
  IconUpload,
  IconX,
} from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import {
  AddToMonitoringModal,
  EscalateReportButton,
  EscalateReportModal,
  EscalateToGdmModal,
  GoogleDocsExportCard,
  ReportFailurePanel,
  SubjectIdentification,
} from '@/components';
import { CopyIntoWorkbenchButton } from '@/components/CopyIntoWorkbench';
import { formatIsoDateString, nowLocaleDateString } from '@/components/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useReport, useReportApprovals, useSubjectCandidates } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import { Subject } from '@/types';
import { saveUrlAs } from '@/utils';
import { ReportContainer } from '../ReportContainer';
import { approvalLabel, ApprovalModal } from './ApprovalModal';
import { PendingActivation } from './PendingActivation';
import { ReportBody } from './ReportBody';

interface ReportViewerProps {
  /** Subject to fetch latest report for (legacy mode) */
  subject?: Subject;
  /** Report ID to fetch directly */
  reportId?: string;
  /**
   * The report config's name for the cover while the hook holds no report
   * (a stage parked on a person, or in flight): the cover renders before
   * the report body exists, and only the page has the metadata by then.
   * A loaded report's own name takes precedence.
   */
  configName?: string | null;
  /**
   * When true, renders only the report content with no UI controls
   */
  printMode?: boolean;
  /**
   * Callback to open the feedback panel
   */
  onOpenFeedback?: () => void;
  /**
   * Fires after an approval lands — the report page uses it to prompt
   * for Network Partner ratings on completed engagements.
   */
  onApproved?: () => void;
}

export function ReportViewer({
  subject,
  reportId: reportIdProp,
  configName = null,
  printMode = false,
  onOpenFeedback,
  onApproved,
}: Readonly<ReportViewerProps>) {
  const { user } = useAuth();
  const {
    report,
    content: reportContent,
    isLoading: reportLoading,
    error: reportError,
    state: reportState,
    status: reportStatus,
    reportId,
    fetchReportById,
  } = useReport(subject?.id ?? '', { level: 'screening', autoFetch: !reportIdProp });

  // If reportId is provided, fetch by report ID directly
  useEffect(() => {
    if (reportIdProp) {
      fetchReportById(reportIdProp);
    }
  }, [reportIdProp, fetchReportById]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Feedback can re-run identification on a report that already shipped. It
  // leaves the report at `processing_feedback` waiting on a candidate
  // choice, so look for unresolved candidates there — otherwise the page
  // polls a report that is waiting on the analyst, forever. Fetched only in
  // that stage; every other report skips the request.
  const inFeedbackStage = reportStatus === 'processing_feedback';
  const { subjects: feedbackCandidates } = useSubjectCandidates(
    inFeedbackStage && reportId ? reportId : null
  );
  const feedbackReidentification =
    inFeedbackStage && feedbackCandidates.some((subject) => !subject.is_resolved);

  const [subjectData, setSubjectData] = useState<Subject | null>(null);

  // Approval state — driven by the report's own approval_status field, then
  // kept in sync via the approvals hook when admins approve/revoke inline.
  const approvalRequired = report?.approval_required ?? 'disabled';
  const reportApprovalStatus = report?.approval_status ?? null;
  const {
    status: approvalStatus,
    isMutating: approvalMutating,
    approve: approveReport,
    revoke: revokeReport,
  } = useReportApprovals({
    reportId,
    initialStatus: reportApprovalStatus,
  });
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [escalateModalOpen, setEscalateModalOpen] = useState(false);
  const [gdmModalOpen, setGdmModalOpen] = useState(false);
  const navigate = useNavigate();

  const isAdmin = user?.isAdministrator ?? false;
  const [monitoringModalOpen, setMonitoringModalOpen] = useState(false);
  const approvalGated = approvalRequired !== 'disabled' && approvalStatus !== 'approved';
  // Clients never see content for a gated report; admins always see content.
  const contentHiddenForClient = !isAdmin && approvalGated;

  useEffect(() => {
    document.title = 'GTI Hub | Ground Truth Intelligence';
  }, [subjectData?.subjectName]);

  useEffect(() => {
    if (subject) {
      setSubjectData(subject);
    }
  }, [subject]);

  // The report header needs the subject name(s) and the report's creation date.
  // When opened by report id (no `subject` prop) neither is on hand, so fetch
  // the report's subjects. This also covers multi-subject reports.
  const [reportSubjects, setReportSubjects] = useState<Subject[] | null>(null);
  useEffect(() => {
    if (!reportId) {
      return;
    }
    let cancelled = false;
    vnextApiClient
      .getReportSubjects(reportId)
      .then((subs) => {
        if (!cancelled) {
          setReportSubjects(subs);
        }
      })
      .catch(() => {
        // Non-fatal — the header falls back to the prop subject / report date.
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  // Prefer the report's own subjects (handles multi-subject + report-id opens);
  // fall back to the prop subject, then a neutral label.
  const reportSubjectNames = (reportSubjects ?? [])
    .map((s) => s.subjectName)
    .filter(Boolean)
    .join(', ');
  const headerSubjectName = reportSubjectNames || subjectData?.subjectName || 'Report';
  // The date is the report's creation date — not the subject's, and never "today".
  const headerDate = formatIsoDateString(report?.created_at) ?? nowLocaleDateString();
  const headerReportLevel = report?.config_name || configName || 'Screening Report';

  // Subjects for the admin-only GDM-3G escalation. Same precedence as the
  // header (report's own subjects, else the prop subject); only those with an
  // id can be escalated.
  const gdmSubjects = (reportSubjects ?? (subjectData ? [subjectData] : []))
    .filter((s): s is Subject & { id: string } => Boolean(s.id))
    .map((s) => ({
      id: s.id,
      subjectName: s.subjectName,
      externalProjectId: s.externalProjectId,
    }));

  // Only subjects actually escalated to a GDM report have anything to export.
  // ``externalProjectId`` is stamped when the escalation creates the platform
  // project, so it is the narrowest honest signal — without it the card would
  // mount, fetch a blend, and find nothing on every report an admin opens.
  const escalatedSubjects = gdmSubjects.filter((s) => Boolean(s.externalProjectId));

  // The Google Docs export belongs to a GDM escalation, and its status is an
  // admin concern — clients see the report itself, not how it was produced.
  const showGoogleDocsExports = isAdmin && escalatedSubjects.length > 0;

  const isLoading = reportLoading;
  // 'submitted' is the client-coarse spelling of triage — self-serve
  // identification is a client-facing state.
  //
  // Read the STATUS, not report.status: while a report is in flight the
  // hook polls without setting `report`, so anything gated on the report
  // object is invisible for exactly the stages that need it most.
  const awaitingIdentification =
    reportStatus === 'triage' || reportStatus === 'submitted' || feedbackReidentification;
  const identificationExhausted = reportError === 'identification_exhausted';
  const isDocx = report?.is_docx === true;
  const showReportContent =
    reportState === 'success' &&
    (isDocx || !!reportContent) &&
    !contentHiddenForClient &&
    !awaitingIdentification;

  const refetchReport = () => {
    if (reportId) {
      fetchReportById(reportId);
    }
  };

  const handleDownloadDocx = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      const { url, filename } = await vnextApiClient.downloadReportDocx(reportId);
      await saveUrlAs(url, filename);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [reportId]);

  const handleDownloadPdf = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      const { url, filename } = await vnextApiClient.downloadReportPdf(reportId);
      await saveUrlAs(url, filename);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [reportId]);

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingDocx, setIsUploadingDocx] = useState(false);

  const handleUploadDocxVersion = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !reportId) {
        return;
      }
      e.target.value = '';
      setIsUploadingDocx(true);
      try {
        await vnextApiClient.uploadDocxVersion(reportId, file);
        refetchReport();
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        setIsUploadingDocx(false);
      }
    },
    [reportId]
  );

  let content;

  if (isLoading) {
    content = <Loader />;
  }

  if (reportState === 'polling') {
    content = (
      <Stack gap="sm" align="center" py="xl">
        <Loader size="md" />
        <Text size="sm" c="dimmed">
          {reportStatus === 'processing_feedback' ? 'Updating report...' : 'Generating report...'}
        </Text>
      </Stack>
    );
  }

  if (awaitingIdentification && reportId) {
    content = (
      <SubjectIdentification
        reportId={reportId}
        fromFeedback={feedbackReidentification}
        onResolved={refetchReport}
        onFailed={refetchReport}
      />
    );
  }

  if (identificationExhausted) {
    content = (
      <Alert
        variant="light"
        color="red"
        icon={<IconAlertCircle size={16} />}
        title="Unable to identify subject"
      >
        <Stack gap="xs">
          <Text size="sm">
            We couldn't confidently identify the subject after 3 clarification attempts. Please
            start a new report with more distinguishing details (employer, location, role, or other
            context).
          </Text>
        </Stack>
      </Alert>
    );
  }

  if (reportError && !awaitingIdentification && !identificationExhausted) {
    content = (
      <Stack gap="md">
        <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
          Error loading report: {reportError}
        </Alert>
        {/* Admins get what actually broke and the action to re-fire it.
            Clients never see raw exceptions or node names. */}
        {isAdmin && reportStatus === 'failed' && reportId && (
          <ReportFailurePanel reportId={reportId} />
        )}
      </Stack>
    );
  }

  if (reportState === 'not_found') {
    content = (
      <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">No report available for this subject yet.</Text>
      </Alert>
    );
  }

  // Client-facing holding message when a report is awaiting approval
  if (reportState === 'success' && contentHiddenForClient) {
    content = (
      <Alert
        variant="light"
        color="blue"
        icon={<IconClock size={16} />}
        title="Report under review"
      >
        This report is currently being reviewed and will be available shortly. Please check back
        later or contact your account manager if you need it urgently.
      </Alert>
    );
  }

  // Pending activation: the report exists but hasn't run. Show ONLY the subject
  // card(s) + a Start button — no report outline/skeleton, no toolbar. Both
  // clients (owning team) and admins can activate; the backend enforces team.
  if (reportState === 'success' && report?.status === 'pending_activation' && reportId) {
    return <PendingActivation reportId={reportId} onActivated={refetchReport} />;
  }

  if (printMode) {
    return (
      <ReportContainer
        subjectName={headerSubjectName}
        reportLevel={headerReportLevel}
        date={headerDate}
        visible={!isDocx}
        contentRef={contentRef}
      >
        {content}
        {showReportContent && (
          <ReportBody
            isDocx={isDocx}
            reportId={reportId ?? undefined}
            markdownContent={reportContent}
            printMode
            isAdmin={isAdmin}
          />
        )}
      </ReportContainer>
    );
  }

  // Admin approve / revoke (the modal handles the note and its errors)
  const handleApprove = async (notes?: string) => {
    await approveReport(notes);
    onApproved?.();
  };

  const showApprovalControls =
    isAdmin && reportState === 'success' && approvalRequired !== 'disabled' && !!reportId;
  const approvalIsApproved = approvalStatus === 'approved';

  // Only show downloads / feedback when there's actual content to act on
  const hasActionableContent = showReportContent;
  // Show the feedback button even before the report is ready so the
  // toolbar isn't empty during the in-flight / awaiting-approval states;
  // disable it until the content is downloadable.
  const reportPending =
    reportState === 'fetching' ||
    reportState === 'polling' ||
    awaitingIdentification ||
    (reportState === 'success' && contentHiddenForClient);
  const showFeedbackButton = hasActionableContent || reportPending;

  // The Admin dropdown bundles the low-frequency analyst actions
  // (approval, revision upload, AI QA) that used to sprawl across the
  // toolbar. Rendered only when at least one member applies.
  const showAdminMenu =
    showApprovalControls ||
    (isAdmin && isDocx && hasActionableContent) ||
    (isAdmin && !!onOpenFeedback && showFeedbackButton);

  return (
    <Stack>
      <Center>
        <Card>
          <Stack gap="xs">
            <Group>
              {/* Clients keep the single Escalate button (their view was
                  fine); admins get the grouped dropdown with the GDM-3G
                  bridge alongside. */}
              {reportId && !isAdmin && (
                <EscalateReportButton
                  report={{ id: reportId, reportConfigId: report?.report_config_id }}
                  variant="light"
                  size="sm"
                />
              )}
              {reportId && isAdmin && (
                <Menu shadow="md" width={230}>
                  <Menu.Target>
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconRocket size={16} />}
                      rightSection={<IconChevronDown size={14} />}
                      data-testid="escalate-menu"
                    >
                      Escalate
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconRocket size={14} />}
                      onClick={() => setEscalateModalOpen(true)}
                      data-testid="escalate-menu-standard"
                    >
                      Escalate
                    </Menu.Item>
                    {/* Short-term bridge: the automated project + Google
                        Docs export workflow. Disabled (not hidden) without
                        eligible subjects so admins can see it exists. */}
                    <Menu.Item
                      color="grape"
                      leftSection={<IconRocket size={14} />}
                      onClick={() => setGdmModalOpen(true)}
                      disabled={gdmSubjects.length === 0}
                      data-testid="escalate-menu-gdm"
                    >
                      Escalate to GDM report
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
              {reportId && isAdmin && (
                <CopyIntoWorkbenchButton reportId={reportId} size="sm" variant="light" />
              )}
              {/* Only on a completed report, and only for admins. `in_qa`
                  looks finished on screen but has not been approved and can
                  still be rejected — enrolling there would start billing
                  against something that may never reach the client. The
                  server enforces the same rule; this only avoids offering an
                  action that would be refused. */}
              {isAdmin && reportId && reportStatus === 'completed' && (
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconEye size={16} />}
                  onClick={() => setMonitoringModalOpen(true)}
                  data-testid="add-to-monitoring"
                >
                  Add to monitoring
                </Button>
              )}
              {/* Docx reports offer PDF + Word; markdown (screening)
                  reports offer PDF only — rendered server-side through
                  the same Gotenberg instance (markdown route). */}
              {hasActionableContent && (
                <Menu shadow="md" width={180}>
                  <Menu.Target>
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconDownload size={16} />}
                      rightSection={<IconChevronDown size={14} />}
                      data-testid="download-menu"
                    >
                      Download
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconFileTypePdf size={14} />}
                      onClick={handleDownloadPdf}
                      data-testid="download-menu-pdf"
                    >
                      PDF
                    </Menu.Item>
                    {isDocx && (
                      <Menu.Item
                        leftSection={<IconFileTypeDocx size={14} />}
                        onClick={handleDownloadDocx}
                        data-testid="download-menu-docx"
                      >
                        Word (.docx)
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
              {/* Analyst-only: clients never upload revisions. The input
                  lives outside the Admin menu so an in-flight file picker
                  survives the dropdown closing. */}
              {isAdmin && isDocx && hasActionableContent && (
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".docx"
                  style={{ display: 'none' }}
                  onChange={handleUploadDocxVersion}
                />
              )}
              {showAdminMenu && (
                <Menu shadow="md" width={260}>
                  <Menu.Target>
                    <Button
                      variant="light"
                      size="sm"
                      color="gray"
                      leftSection={<IconSettings size={16} />}
                      rightSection={<IconChevronDown size={14} />}
                      loading={approvalMutating || isUploadingDocx}
                      data-testid="admin-menu"
                    >
                      Admin
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {showApprovalControls && (
                      <Menu.Item
                        leftSection={
                          approvalIsApproved ? (
                            <IconCheck size={14} color="var(--mantine-color-green-6)" />
                          ) : approvalStatus === 'revoked' ? (
                            <IconX size={14} color="var(--mantine-color-red-6)" />
                          ) : (
                            <IconClock size={14} color="var(--mantine-color-yellow-7)" />
                          )
                        }
                        onClick={() => setApprovalModalOpen(true)}
                        data-testid="admin-menu-approval"
                      >
                        <Stack gap={0}>
                          <Text size="sm">
                            {approvalIsApproved ? 'Revoke approval…' : 'Approve report…'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {approvalLabel(approvalStatus)}
                          </Text>
                        </Stack>
                      </Menu.Item>
                    )}
                    {isAdmin && isDocx && hasActionableContent && (
                      <Menu.Item
                        leftSection={<IconUpload size={14} />}
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={isUploadingDocx}
                        data-testid="admin-menu-upload"
                      >
                        {isUploadingDocx ? 'Uploading…' : 'Upload revised .docx'}
                      </Menu.Item>
                    )}
                    {onOpenFeedback && showFeedbackButton && (
                      <Menu.Item
                        leftSection={<IconSparkles size={14} />}
                        onClick={onOpenFeedback}
                        disabled={!hasActionableContent}
                        data-testid="admin-menu-ai-qa"
                      >
                        AI QA
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Stack>
        </Card>
      </Center>

      {/* Menu-triggered modals — mounted outside the dropdowns so they
          survive the menu closing on item click. */}
      {reportId && (
        <EscalateReportModal
          opened={escalateModalOpen}
          onClose={() => setEscalateModalOpen(false)}
          report={{ id: reportId, reportConfigId: report?.report_config_id }}
          onEscalated={(result) =>
            // A manually-fulfilled type raised a triage request rather than a
            // report; this menu is admin-only, so both destinations are open.
            navigate(
              result.escalated_to_report_id
                ? `/reports/${result.escalated_to_report_id}`
                : `/admin/triage/${result.escalated_to_report_request_id}`
            )
          }
        />
      )}
      {showGoogleDocsExports && (
        <Stack gap="xs">
          {escalatedSubjects.map((s) => (
            <GoogleDocsExportCard
              key={s.id}
              subjectId={s.id}
              subjectName={escalatedSubjects.length > 1 ? s.subjectName : undefined}
            />
          ))}
        </Stack>
      )}
      {reportId && (
        <EscalateToGdmModal
          opened={gdmModalOpen}
          onClose={() => setGdmModalOpen(false)}
          reportId={reportId}
          subjects={gdmSubjects}
        />
      )}
      {reportId && (
        <AddToMonitoringModal
          opened={monitoringModalOpen}
          onClose={() => setMonitoringModalOpen(false)}
          reportId={reportId}
        />
      )}
      <ApprovalModal
        opened={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        approved={approvalIsApproved}
        mutating={approvalMutating}
        onConfirm={approvalIsApproved ? revokeReport : handleApprove}
      />
      <ReportContainer
        subjectName={headerSubjectName}
        reportLevel={headerReportLevel}
        date={headerDate}
        visible={!isDocx}
        contentRef={contentRef}
      >
        {content}
        {showReportContent && (
          <ReportBody
            isDocx={isDocx}
            reportId={reportId ?? undefined}
            markdownContent={reportContent}
            isAdmin={isAdmin}
          />
        )}
      </ReportContainer>
    </Stack>
  );
}
